import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, animate, useMotionValue, useSpring } from "framer-motion";
import { CheckCircle2, Plus } from "lucide-react";
import { EASE } from "@/components/motion";

const SCORE = 765;
const MAX = 850;
const R = 52;
const C = 2 * Math.PI * R;

const BANDS = [
  { label: "Poor", color: "#D92D20" },
  { label: "Fair", color: "#E8A33D" },
  { label: "Good", color: "#C9A227" },
  { label: "Very Good", color: "#4C9A6E" },
  { label: "Excellent", color: "#0C6B4F" },
];

const FACTORS = [
  { label: "Payment History", pct: 100, tag: "Excellent", color: "#0C6B4F" },
  { label: "Credit Utilization", pct: 82, tag: "Very Good", color: "#4C9A6E" },
  { label: "Credit Age", pct: 94, tag: "Excellent", color: "#0C6B4F" },
];

export default function HeroScoreCard() {
  const [score, setScore] = useState(0);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 16 });
  const sry = useSpring(ry, { stiffness: 120, damping: 16 });

  useEffect(() => {
    const controls = animate(0, SCORE, {
      duration: 2.2,
      delay: 0.7,
      ease: EASE,
      onUpdate: (v) => setScore(Math.round(v)),
    });
    return () => controls.stop();
  }, []);

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - rect.left) / rect.width - 0.5) * 10);
    rx.set(-((e.clientY - rect.top) / rect.height - 0.5) * 10);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 48 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
      className="relative max-w-md w-full mx-auto lg:mx-0 lg:justify-self-end"
      style={{ transformPerspective: 1200 }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="hero-score-card-wrap"
    >
      <motion.div
        style={{ rotateX: srx, rotateY: sry }}
        className="relative bg-white border border-[#DFE5DC] rounded-3xl shadow-[0_30px_80px_-24px_rgba(15,61,46,0.22)] p-7 md:p-8"
        data-testid="hero-score-card"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-[#0F1F1A]">Your Credit Score</h3>
          <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#8A978D]">Updated today</span>
        </div>

        <div className="flex justify-center my-5">
          <div className="relative">
            <svg width="160" height="160" viewBox="0 0 120 120">
              <defs>
                <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#C9A227" />
                  <stop offset="100%" stopColor="#0C6B4F" />
                </linearGradient>
              </defs>
              <circle cx="60" cy="60" r={R} fill="none" stroke="#EDF2EA" strokeWidth="9" />
              <motion.circle
                cx="60"
                cy="60"
                r={R}
                fill="none"
                stroke="url(#scoreGrad)"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={C}
                initial={{ strokeDashoffset: C }}
                animate={{ strokeDashoffset: C * (1 - SCORE / MAX) }}
                transition={{ duration: 2.2, delay: 0.7, ease: EASE }}
                transform="rotate(-90 60 60)"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-5xl text-[#0F1F1A] leading-none" data-testid="score-value">{score}</span>
              <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#8A978D] mt-1">out of {MAX}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-1 mb-7" data-testid="score-bands">
          {BANDS.map((b, i) => (
            <div key={b.label} className="text-center">
              <div className="h-1 rounded-full mb-2 mx-1" style={{ background: b.color, opacity: i === 3 ? 1 : 0.25 }} />
              <p className={`text-[10px] font-mono2 uppercase tracking-wide ${i === 3 ? "text-[#0F1F1A] font-semibold" : "text-[#8A978D]"}`}>
                {b.label}
              </p>
            </div>
          ))}
        </div>

        <div className="mb-7">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8A978D] mb-4">Credit Score Factors</p>
          <div className="flex flex-col gap-4">
            {FACTORS.map((f, i) => (
              <div key={f.label}>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-[#5C6B62]">{f.label}</span>
                  <span className="font-mono2" style={{ color: f.color }}>{f.tag}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#EDF2EA] overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: f.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${f.pct}%` }}
                    transition={{ duration: 1.4, delay: 1 + i * 0.2, ease: EASE }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <Link
          to="/#apply"
          data-testid="score-card-cta"
          className="flex items-center justify-center gap-2 bg-[#0C6B4F] text-white text-xs font-semibold uppercase tracking-[0.2em] py-4 rounded-xl hover:bg-[#0A5C43] active:scale-[0.99] transition-[background-color,transform] duration-300"
        >
          <Plus className="w-4 h-4" /> Compare My Options
        </Link>
        <p className="text-center font-mono2 text-[10px] text-[#8A978D] mt-3 uppercase tracking-[0.15em]">
          Sample illustration — your score may vary
        </p>
      </motion.div>

      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-5 -right-2 md:-right-6 bg-white border border-[#DFE5DC] rounded-2xl shadow-lg px-4 py-3 flex items-center gap-2.5"
        data-testid="approved-badge"
      >
        <CheckCircle2 className="w-5 h-5 text-[#0C6B4F]" strokeWidth={2} />
        <div>
          <p className="text-xs font-semibold text-[#0F1F1A] leading-none">Approved</p>
          <p className="font-mono2 text-[10px] text-[#8A978D] mt-1">Excellent</p>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
        className="absolute -bottom-6 -left-3 md:-left-10 bg-white border border-[#DFE5DC] rounded-2xl shadow-lg px-5 py-3"
        data-testid="loan-offer-chip"
      >
        <p className="font-display text-2xl text-[#0C6B4F] leading-none">$25K</p>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#8A978D] mt-1">Loan Offer</p>
      </motion.div>
    </motion.div>
  );
}
